// eslint-disable-next-line no-restricted-imports
import { ToastStack, ToastStackProvider, useToastStack } from "@bimone/bimtrackreactcomponents";
import type { PropsWithChildren, ReactElement } from "react";
import React, { createContext, useCallback, useMemo } from "react";
import type { NoProps } from "@shared/types/NoProps";
import type { UploadedItem } from "@core/folderItems/models/UploadedItem";
import { ToastActions } from "@shared/toasts/ToastActions";
import { generateUUID } from "@core/shared/services/UUID";

interface BaseToastProps { action: ToastActions, message: string }

interface GenericToastProps extends BaseToastProps { items?: never, item?: never, id?: string }
interface SingleItemToastProps extends BaseToastProps { item: UploadedItem, items?: never, id?: never }
interface MultipleItemToastProps extends BaseToastProps { item?: never, items: UploadedItem[], id?: never}

export type ToastProps = GenericToastProps | SingleItemToastProps | MultipleItemToastProps;

export interface ToastsContextType {
    displayToast: (props: ToastProps) => void;
}

export const ToastContext = createContext<ToastsContextType | undefined>(undefined);

export const ToastProvider = ({ children }: PropsWithChildren<NoProps>): ReactElement => {

    return (
      <ToastStackProvider>
        <ToastStack />
        <UpdateTableToastProvider>
          {children}
        </UpdateTableToastProvider>
      </ToastStackProvider>
    );
};

const UpdateTableToastProvider = ({ children }: PropsWithChildren<NoProps>): ReactElement => {
    const { addToast } = useToastStack();

    const multipleItemToast = useCallback(({ items, message, action }: MultipleItemToastProps): void => {
        const id = items.reduce((acc, item) => {
            return acc + "|" + item.itemId.uniqueKey;
        }, "");

      addToast({ id: `${action}_${id}`, message });
    }, [addToast]);

    const singleItemToast = useCallback(({ item, message, action }: SingleItemToastProps): void => {
        const id = item.itemId.uniqueKey;
      addToast({ id: `${action}_${id}`, message });
    }, [addToast]);

    const displayToast = useCallback(({ items, item, id, message, action = ToastActions.Generic }: ToastProps): void => {
        if(items) {
            multipleItemToast({ items, action, message });
        }
        else if(item) {
            singleItemToast({ item, action, message });
        }
        else {
          addToast({ id: `${action}_${id ?? generateUUID()}`, message });
        }
    }, [addToast, multipleItemToast, singleItemToast]);

    const value = useMemo(() =>
            ({ displayToast }),
        [displayToast]);

    return (
      <ToastContext.Provider
        value={value}
      >
        {children}
      </ToastContext.Provider>
    );
};